import { useMemo, useState } from 'react';
import { formatMoney } from '@core/money/format';
import { asMoney } from '@core/money/types';
import type { Document, Payment } from '@data/db/types';
import { Button } from '@ui/components/Button';
import { EmptyState, ErrorState, LoadingState } from '@ui/components/States';
import { Field, Input, Select } from '@ui/components/Field';
import { PageHeader } from '@ui/components/PageHeader';
import { toast } from '@ui/components/Toaster';
import { useSaveReceipt, useReceiptData } from './useReceipts';

const METHODS: readonly Payment['method'][] = ['CASH', 'BANK', 'UPI', 'NEFT', 'RTGS', 'CHEQUE', 'CARD', 'DD'];
const TODAY = new Date().toISOString().slice(0, 10);
const money = (paise: number) => formatMoney(asMoney(paise));

function rupeesToPaise(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(trimmed)) return null;
  const [rupees = '', fraction = ''] = trimmed.split('.');
  const paise = Number(rupees) * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(paise) && paise > 0 ? paise : null;
}

function invoiceLabel(document: Document): string {
  return `${document.docNumber} · outstanding ${money(document.balancePaise)}`;
}

export function ReceiptsScreen() {
  const { businessQuery, data } = useReceiptData();
  const save = useSaveReceipt();
  const [customerId, setCustomerId] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(TODAY);
  const [method, setMethod] = useState<Payment['method']>('CASH');
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');

  const invoiceData = data.data;
  const invoicesForCustomer = useMemo(() => {
    const customer = invoiceData?.customers.find((party) => party.id === customerId);
    if (!invoiceData || !customer) return [];
    return invoiceData.invoices.filter((invoice: Document) =>
      invoice.customerId === customerId &&
      (invoice.status === 'ISSUED' || invoice.status === 'PARTIALLY_SETTLED') &&
      invoice.balancePaise > 0,
    );
  }, [invoiceData, customerId]);
  const selectedInvoice = invoicesForCustomer.find((invoice) => invoice.id === documentId);

  function chooseCustomer(id: string) {
    setCustomerId(id);
    setDocumentId('');
    setAmount('');
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const paise = rupeesToPaise(amount);
    if (!customerId || !documentId) {
      setError('Choose a customer and an unpaid invoice.');
      return;
    }
    if (paise === null) {
      setError('Enter an amount greater than zero, with up to two decimal places.');
      return;
    }
    if (selectedInvoice && paise > selectedInvoice.balancePaise) {
      setError(`This invoice has ${money(selectedInvoice.balancePaise)} outstanding.`);
      return;
    }
    setError('');
    try {
      const receipt = await save.mutateAsync({
        customerId,
        documentId,
        paymentDate,
        amountPaise: paise,
        method,
        reference,
      });
      toast({
        title: `Receipt ${receipt.paymentNumber} recorded`,
        description: selectedInvoice && paise === selectedInvoice.balancePaise
          ? 'The invoice is now settled.'
          : 'The amount has been applied to the invoice.',
        tone: 'success',
      });
      setDocumentId('');
      setAmount('');
      setReference('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The receipt could not be recorded.');
    }
  }

  if (businessQuery.isError) {
    return <div className="p-4"><ErrorState title="No active business" message="Choose or set up a business before recording receipts." /></div>;
  }
  if (businessQuery.isLoading || (data.isLoading && !data.data)) return <LoadingState label="Loading receipts…" />;
  if (data.isError) return <div className="p-4"><ErrorState title="Could not load receipts" message="Your local receipt data could not be read." onRetry={() => void data.refetch()} /></div>;
  if (!data.data) return null;

  const { customers, invoices, receipts } = data.data;
  const unpaidInvoiceCount = invoices.filter((invoice) =>
    (invoice.status === 'ISSUED' || invoice.status === 'PARTIALLY_SETTLED') && invoice.balancePaise > 0,
  ).length;

  return (
    <div className="flex flex-col p-4 text-slate-100">
      <PageHeader title="Receipts" description="Record money received from a customer and apply it to an invoice." />
      <div className="grid gap-4 xl:grid-cols-[minmax(20rem,0.8fr)_minmax(28rem,1.2fr)]">
        <section className="rounded-lg border border-slate-800 bg-slate-900/50 p-4">
          <h2 className="mb-3 text-sm font-semibold">Record a receipt</h2>
          {unpaidInvoiceCount === 0 ? (
            <EmptyState title="No unpaid invoices" description="Issue an invoice first. Receipts can be recorded against issued invoices only." />
          ) : customers.length === 0 ? (
            <EmptyState title="No customers found" description="Add a customer before recording a receipt." />
          ) : (
            <form className="flex flex-col gap-3" onSubmit={(event) => void submit(event)}>
              <Field label="Customer" required>
                {(wiring) => (
                  <Select {...wiring} data-testid="receipt-customer" value={customerId} onChange={(event) => chooseCustomer(event.target.value)}
                    options={customers.map((customer) => ({ value: customer.id, label: customer.name }))}
                    placeholder="Choose a customer" required />
                )}
              </Field>
              <Field label="Invoice" required>
                {(wiring) => (
                  <Select {...wiring} data-testid="receipt-invoice" value={documentId} onChange={(event) => setDocumentId(event.target.value)}
                    options={invoicesForCustomer.map((invoice) => ({ value: invoice.id, label: invoiceLabel(invoice) }))}
                    placeholder={customerId ? 'Choose an unpaid invoice' : 'Choose a customer first'}
                    disabled={!customerId} required />
                )}
              </Field>
              {selectedInvoice && (
                <div className="rounded border border-slate-800 bg-slate-950/70 px-3 py-2 text-sm" aria-live="polite">
                  <div className="flex justify-between gap-3"><span className="text-slate-400">Invoice total</span><span>{money(selectedInvoice.grandTotalPaise)}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-400">Already received</span><span>{money(selectedInvoice.allocatedPaise)}</span></div>
                  <div className="mt-1 flex justify-between gap-3 border-t border-slate-800 pt-1 font-medium"><span>Outstanding</span><span>{money(selectedInvoice.balancePaise)}</span></div>
                </div>
              )}
              <Field label="Amount received (₹)" hint="Enter a part-payment or the full outstanding amount." required>
                {(wiring) => <Input {...wiring} data-testid="receipt-amount" inputMode="decimal" autoComplete="off" placeholder="0.00" value={amount} onChange={(event) => setAmount(event.target.value)} required />}
              </Field>
              <Field label="Date received" required>
                {(wiring) => <Input {...wiring} type="date" value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} required />}
              </Field>
              <Field label="Payment method" required>
                {(wiring) => <Select {...wiring} data-testid="receipt-method" value={method} onChange={(event) => setMethod(event.target.value as Payment['method'])}
                  options={METHODS.map((value) => ({ value, label: value === 'UPI' ? 'UPI' : value }))} required />}
              </Field>
              <Field label="Reference" hint="Optional: transaction ID, cheque number, etc.">
                {(wiring) => <Input {...wiring} data-testid="receipt-reference" value={reference} onChange={(event) => setReference(event.target.value)} maxLength={100} />}
              </Field>
              {error && <p role="alert" className="rounded border border-red-900 bg-red-950/40 p-2 text-sm text-red-200">{error}</p>}
              <div className="flex justify-end">
                <Button type="submit" variant="primary" loading={save.isPending} loadingText="Recording…">Record receipt</Button>
              </div>
            </form>
          )}
        </section>

        <section className="min-w-0 rounded-lg border border-slate-800 bg-slate-900/50 p-4">
          <div className="mb-3 flex items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold">Recent receipts</h2>
            <span className="text-xs text-slate-500">{receipts.length} recorded</span>
          </div>
          {receipts.length === 0 ? (
            <EmptyState title="No receipts recorded yet" description="Receipts you record will appear here with their invoice allocation." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-800 text-xs uppercase text-slate-500">
                  <tr><th className="py-2 pr-3">Date / Receipt</th><th className="py-2 pr-3">Customer</th><th className="py-2 pr-3">Invoice</th><th className="py-2 text-right">Amount</th></tr>
                </thead>
                <tbody>
                  {receipts.map(({ payment, allocations }) => (
                    <tr key={payment.id} className="border-b border-slate-800/60 align-top last:border-0">
                      <td className="py-2 pr-3"><div>{payment.paymentDate}</div><div className="text-xs text-slate-500">{payment.paymentNumber} · {payment.method}</div>{payment.reference && <div className="text-xs text-slate-500">Ref: {payment.reference}</div>}</td>
                      <td className="py-2 pr-3">{payment.partySnapshot.name}</td>
                      <td className="py-2 pr-3">{allocations.length ? allocations.map((allocation) => <div key={allocation.id}>{allocation.documentNumber} <span className="text-xs text-slate-500">({money(allocation.amountPaise)})</span></div>) : <span className="text-slate-500">On account</span>}</td>
                      <td className="py-2 text-right font-medium">{money(payment.amountPaise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
